"use client";

import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCcw, RotateCw, ZoomIn, ZoomOut } from "lucide-react";

interface FotoEditorDialogProps {
  imageSrc: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (blob: Blob) => void;
}

async function getCroppedBlob(imageSrc: string, pixelCrop: Area, rotation: number): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = imageSrc;
  });

  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));

  // Bounding box of the rotated image
  const bBoxW = Math.round(image.width * cos + image.height * sin);
  const bBoxH = Math.round(image.width * sin + image.height * cos);

  // Step 1: draw the rotated image centered in its bounding box
  const rotCanvas = document.createElement("canvas");
  rotCanvas.width = bBoxW;
  rotCanvas.height = bBoxH;
  const rotCtx = rotCanvas.getContext("2d")!;
  rotCtx.translate(bBoxW / 2, bBoxH / 2);
  rotCtx.rotate(rotRad);
  rotCtx.translate(-image.width / 2, -image.height / 2);
  rotCtx.drawImage(image, 0, 0);

  // Step 2: extract exactly the crop area (pixelCrop is in rotated-image space)
  const cropData = rotCtx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  // Step 3: paste crop into a temp canvas, then scale to 600×600 output
  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = pixelCrop.width;
  cropCanvas.height = pixelCrop.height;
  cropCanvas.getContext("2d")!.putImageData(cropData, 0, 0);

  const out = document.createElement("canvas");
  out.width = 600;
  out.height = 600;
  out.getContext("2d")!.drawImage(cropCanvas, 0, 0, 600, 600);

  return new Promise<Blob>((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas vacío"))),
      "image/jpeg",
      0.9,
    );
  });
}

export function FotoEditorDialog({ imageSrc, open, onOpenChange, onApply }: FotoEditorDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  async function handleApply() {
    if (!croppedArea) return;
    const blob = await getCroppedBlob(imageSrc, croppedArea, rotation);
    onApply(blob);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden rounded-xl" showCloseButton={false}>
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
          <p className="text-sm font-semibold">Editar foto</p>
        </DialogHeader>

        <div className="relative w-full" style={{ height: 300, background: "#000" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-4 py-3 space-y-3 border-t border-border">
          <div className="flex items-center gap-2">
            <ZoomOut className="size-4 text-muted-foreground shrink-0" />
            <input
              type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1 accent-primary"
            />
            <ZoomIn className="size-4 text-muted-foreground shrink-0" />
          </div>
          <div className="flex justify-center gap-3">
            <button type="button" onClick={() => setRotation(r => r - 90)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
              <RotateCcw className="size-3.5" /> -90°
            </button>
            <button type="button" onClick={() => setRotation(r => r + 90)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
              <RotateCw className="size-3.5" /> +90°
            </button>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button size="sm" className="flex-1" onClick={handleApply}>
              Aplicar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
